import {
  App as AntdApp,
  Button,
  Dropdown,
  Flex,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { MoreHorizontal, Pencil, Plus, UserMinus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserGroup } from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { GroupMembersCell } from 'src/features/users/components'
import { useWorkspaceUserGroups } from 'src/features/users/hooks'
import { useQueryParams, useTableSorter } from 'src/hooks'
import { defaultFilters, useShowApiErrors } from 'src/utils'

import {
  partitionRolesByApp,
  useAppGroupRoleMutation
} from '../hooks/useAppGroupRoles'

import { AppGroupCreateDrawer } from './AppGroupCreateDrawer'
import { AppGroupRolesModal } from './AppGroupRolesModal'

const { Text } = Typography

const DEFAULT_ORDERING = '-date_created'
const ALL_GROUPS_QUERY = { per_page: 500 } as const

type Props = {
  applicationId: number
  organizationId: number
}

type ModalState =
  | { mode: 'closed' }
  | { mode: 'add' }
  | { mode: 'edit'; group: UserGroup }

export const AppGroupsPanel = ({ applicationId, organizationId }: Props) => {
  const { t } = useTranslation()
  const { modal, message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()

  const params = useQueryParams()
  const sorter = useTableSorter()
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' })
  const [createOpen, setCreateOpen] = useState(false)

  const { groups, total, isLoading, isFetching, isError, refetch } =
    useWorkspaceUserGroups({
      workspaceId: organizationId,
      query: {
        ...defaultFilters,
        ...params,
        ordering: String(params.ordering ?? '') || DEFAULT_ORDERING,
        role_application: applicationId
      }
    })

  const { groups: allGroups } = useWorkspaceUserGroups({
    workspaceId: organizationId,
    query: ALL_GROUPS_QUERY
  })

  const { setGroupRoles, isPending } = useAppGroupRoleMutation(
    organizationId,
    applicationId
  )

  const grantedIds = useMemo(
    () => new Set(groups.map((group) => group.id)),
    [groups]
  )

  const candidates = useMemo(
    () => allGroups.filter((group) => !grantedIds.has(group.id)),
    [allGroups, grantedIds]
  )

  const submit = async (groupId: number, roleIds: number[]) => {
    try {
      await setGroupRoles(groupId, roleIds)
      void message.success(t('workspace.apps.groups.messages.rolesUpdated'))
      setModalState({ mode: 'closed' })
    } catch (error) {
      showApiErrors(error)
    }
  }

  const confirmRevoke = (group: UserGroup) => {
    modal.confirm({
      title: t('workspace.apps.groups.revoke.title'),
      content: t('workspace.apps.groups.revoke.content', { name: group.name }),
      okText: t('workspace.apps.groups.revoke.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('workspace.apps.groups.modal.cancel'),
      onOk: async () => {
        try {
          await setGroupRoles(group.id, [])
          void message.success(t('workspace.apps.groups.messages.revoked'))
        } catch (error) {
          showApiErrors(error)
        }
      }
    })
  }

  const columns: TableColumnsType<UserGroup> = [
    {
      title: t('workspace.apps.groups.columns.name'),
      key: 'name',
      fixed: 'left',
      dataIndex: 'name',
      width: 220,
      ellipsis: true,
      render: (name: string) => (
        <Text strong style={{ fontSize: 13 }} ellipsis={{ tooltip: name }}>
          {name}
        </Text>
      )
    },
    {
      title: t('workspace.apps.groups.columns.members'),
      key: 'users',
      width: 190,
      render: (_, group) => <GroupMembersCell members={group.users ?? []} />
    },
    {
      // Only this app's roles — the rest belong to screens that can see them.
      title: t('workspace.apps.groups.columns.roles'),
      key: 'roles',
      render: (_, group) => {
        const { own, foreign } = partitionRolesByApp(group, applicationId)

        return (
          <Flex gap={4} wrap="wrap" align="center">
            {own.map((role) => (
              <Tag key={role.id} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {role.name}
              </Tag>
            ))}
            {foreign.length > 0 ? (
              <Tooltip
                title={t('workspace.apps.groups.columns.otherAppsHint', {
                  roles: foreign.map((role) => role.name).join(', ')
                })}
              >
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('workspace.apps.groups.columns.otherApps', {
                    count: foreign.length
                  })}
                </Text>
              </Tooltip>
            ) : null}
          </Flex>
        )
      }
    },
    {
      title: t('workspace.apps.groups.columns.updated'),
      key: 'date_updated',
      dataIndex: 'date_updated',
      width: 140,
      render: (value: string) => (
        <DateTimeCell
          value={value}
          variant="relative"
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center' as const,
      render: (_, group) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'edit',
                icon: <Pencil size={14} />,
                label: t('workspace.apps.groups.actions.editRoles'),
                onClick: () => setModalState({ mode: 'edit', group })
              },
              {
                key: 'revoke',
                icon: <UserMinus size={14} />,
                label: t('workspace.apps.groups.actions.revoke'),
                danger: true,
                onClick: () => confirmRevoke(group)
              }
            ]
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreHorizontal size={14} />}
            aria-label={t('users.groups.actions.more')}
          />
        </Dropdown>
      )
    }
  ]

  if (isLoading) return <ContentSpinner minHeight={400} />
  if (isError) return <QueryErrorAlert onRetry={() => void refetch()} />

  return (
    <Flex vertical gap={14}>
      <ProTableContainer<UserGroup>
        tableIdentifier="app-groups"
        searchPlaceholder={t('users.groups.searchPlaceholder')}
        loading={isFetching}
        columns={columns}
        data={{ count: total, results: groups }}
        rowKey={(group) => group.id}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        scroll={{ x: 820 }}
        tableLayout="fixed"
        rightChildren={
          <Flex align="center" gap={8}>
            <Button
              size="small"
              icon={<Plus size={14} />}
              onClick={() => setCreateOpen(true)}
            >
              {t('workspace.apps.groups.actions.create')}
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<Plus size={14} />}
              onClick={() => setModalState({ mode: 'add' })}
            >
              {t('workspace.apps.groups.actions.add')}
            </Button>
          </Flex>
        }
      />

      <AppGroupCreateDrawer
        open={createOpen}
        organizationId={organizationId}
        applicationId={applicationId}
        onClose={() => setCreateOpen(false)}
      />

      <AppGroupRolesModal
        open={modalState.mode !== 'closed'}
        organizationId={organizationId}
        applicationId={applicationId}
        group={modalState.mode === 'edit' ? modalState.group : null}
        candidates={candidates}
        saving={isPending}
        onCancel={() => setModalState({ mode: 'closed' })}
        onSubmit={submit}
      />
    </Flex>
  )
}
